"use client";
import { useTranslations } from "next-intl";
import { DataList, Flex, Skeleton } from "@radix-ui/themes";
import type { DataElement, DataInputParams, GeoMapProps } from "./inputType";
import { GetDataInputElement, getFieldValue, getInputFieldValue } from "./inputFunction";
import { TransitionType } from "@/components/layout/layoutType";
import { Dispatch, SetStateAction, TouchEvent as ReacthTouch, useEffect, useMemo, useRef, useState, useContext } from "react";
import { cn } from "@/lib/utils";
import { chartCoverDivstyle, nivoTheme, setTimeoutShort } from "../list/defaultType";
import { Debug } from "@/util/debug";
import { Button, Skeleton as AntSkeleton, Switch, FormInstance } from "antd";
import { getDataKeyLabel } from "@/util/datautil";
import { GetDataLabel, GetDataValue } from "../data/dataInput";

import Icon from "@/components/common/icon/icon";
// import { useSpring, useSprings, animated, SpringValue, config } from "@react-spring/web";
import { enLocale } from "@/i18n/routing";
import { easeLinear, easeQuadIn, easeQuadOut, easeExpOut } from "d3-ease";
import { checkAndroid, fileSaved, fileSaveError } from "@/util/apputil";
// const Icon = dynamic(() => import("@/components/common/icon/icon"), { ssr: false });
const defaultDuration = 500;
const maxScale = 40000;
const defaultClassName = "flex-col h-[16rem] md:h-[30rem] w-full";
const fillColor = "var(--accent-a5)";

import { ChoroplethBoundFeature, ResponsiveChoropleth } from "@nivo/geo";
import { checkEmpty } from "@/util/stringutil";
import { AlertContext } from "@/components/context/alertContext";
import { exportChartAsPNG } from "@/util/fileutil";
// import dynamic from "next/dynamic";
// const ResponsiveChoropleth = dynamic(
//   () =>
//     import("@nivo/geo")
//       .then((mod) => mod.ResponsiveChoropleth)
//       .catch(() => {
//         const DummyComponent = () => <></>;
//         return DummyComponent;
//       }),
//   { ssr: false },
// );

//const country = dynamic(() => import("@datas/world_countries.json"), { ssr: false });
type GeoFeature = {
  id: string;
  type: string;
  properties: Record<string, string>;
  geometry: {
    type: string;
    coordinates: any[][][];
  };
};
type proAniProps = {
  scale?: number;
  rotation?: [number, number, number];
  fromScale?: number;
  fromRotation?: [number, number, number];
  easeType?: "easeLinear" | "easeQuadIn" | "easeQuadOut" | "easeExpOut";
  duration?: number;
};

//import countries from "@datas/world_countries.json";
const countries: {
  type: string;
  features: GeoFeature[];
} = { type: "FeatureCollection", features: [] };

const latlngDistance = (fromLat: number, fromLng: number, toLat: number, toLng: number) => {
  const dLat = ((toLat - fromLat) * Math.PI) / 180;
  const dLng = ((toLng - fromLng) * Math.PI) / 180;

  const lat1 = (fromLat * Math.PI) / 180;
  const lat2 = (toLat * Math.PI) / 180;

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 6371;
};

const latlngDuration = (fromLat: number, fromLng: number, toLat: number, toLng: number) => {
  const minDuration = 200;
  const maxDuration = 500;
  const maxDistanceKm = 5000;
  return fromLat == toLat && fromLng === toLng
    ? minDuration
    : Math.round(
        Math.max(
          minDuration,
          Math.min(
            maxDuration,
            minDuration +
              (Math.log(1 + Math.min(latlngDistance(fromLat, fromLng, toLat, toLng), maxDistanceKm)) / Math.log(1 + maxDistanceKm)) *
                (maxDuration - minDuration),
          ),
        ),
      );
};
const normalizeAngle = (angle: number): number => {
  return ((((angle + 180) % 360) + 360) % 360) - 180;
};
type GeoMapItemProps = GeoMapProps & {
  t: TransitionType;
  form: FormInstance<any> | undefined;
  scale: number | undefined;
  filter: boolean;
  setSelectCountry: Dispatch<SetStateAction<string | string[] | undefined>>;
  imageDownload?: boolean | string;
  value?: any;
  values?: any;
  locale?: string;
  onClick?: ((event: any) => void) | undefined;
};
export function DataGeoMapItem(params: GeoMapItemProps) {
  //강조 표시 국가 목록
  const getCountryList = () => {
    // Debug(params.country_list);
    return (
      (typeof params.country_list === "string" ? getFieldValue(params.form, params.country_list, params.values) : params.country_list) ??
      (params.country_label ? Object.keys(params.country_label) : [])
    );
  };
  const getSelectCountryList = () => {
    // Debug(params.select_country);
    return typeof params.select_country !== "string" ? params.select_country : [params.select_country];
  };
  const selectCountryRef = useRef<string | string[] | undefined>(params.select_country);

  const [countries, setCountries] = useState<{
    type: string;
    features: GeoFeature[];
  }>({ type: "FeatureCollection", features: [] });
  const [filter, setFilter] = useState(false);
  const [selectFeatures, setSelectFeature] = useState(countries.features.map((feature) => feature));
  const [data, setData] = useState(
    selectFeatures.map((select) => {
      return {
        id: select.id,
        value: 70,
        color: fillColor,
        properties: select.properties,
        label:
          params.locale === enLocale
            ? select.properties.name
            : ((select.properties as any)["name_" + params.locale] ?? select.properties.name),
      };
    }),
  );
  const minScaleRef = useRef<number>(0);
  const [scale, setScale] = useState(minScaleRef.current);
  const scaleRef = useRef<number>(scale);
  const [rotation, setRotation] = useState<[number, number, number]>([0, 0, 0]);
  const [isTouching, setIsTouching] = useState(false);

  const layoutRef = useRef<HTMLDivElement>(null);
  const aniIdRef = useRef<number | null>(null);
  const lastPinchDistance = useRef<number | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  //이동 애니메이션
  const animateProjection = (to: proAniProps) => {
    const toObj = {
      toScale: to.scale ?? scale,
      toRot: to.rotation ?? rotation,
      duration: to.duration ?? defaultDuration,
    };
    // 진행 중인 애니메이션 취소
    if (aniIdRef.current !== null) {
      cancelAnimationFrame(aniIdRef.current);
    }

    const fromScale = to.fromScale ?? scale;
    const fromRot = to.fromRotation ?? rotation;
    const start = performance.now();

    function frame(now: number) {
      const elapsed = now - start;
      let t = Math.min(elapsed / toObj.duration, 1);
      switch (to.easeType) {
        case "easeLinear":
          t = easeLinear(t);
          break;
        case "easeQuadIn":
          t = easeQuadIn(t);
          break;
        case "easeQuadOut":
          t = easeQuadOut(t);
          break;
        case "easeExpOut":
        default:
          t = easeExpOut(t);
          break;
      }
      // (easeLinear, easeQuadIn, easeQuadOut, easeExpOut);

      // 보간된 값 계산
      const currentScale = fromScale + (toObj.toScale - fromScale) * t;
      const currentRot: [number, number, number] = [
        fromRot[0] + (toObj.toRot[0] - fromRot[0]) * t,
        fromRot[1] + (toObj.toRot[1] - fromRot[1]) * t,
        fromRot[2] + (toObj.toRot[2] - fromRot[2]) * t,
      ];
      setScale(currentScale);
      if (t < 1) {
        setRotation(currentRot);
        aniIdRef.current = requestAnimationFrame(frame);
      } else {
        const lamda = normalizeAngle(currentRot[0]);
        setRotation([lamda, currentRot[1], currentRot[2]]);
        aniIdRef.current = null;
      }
    }
    aniIdRef.current = requestAnimationFrame(frame);
  };
  //줌인아웃 관련 함수 시작
  const handleWheelScale = (e: WheelEvent) => {
    const factor = 1.05;
    setScale((s) => {
      if (e.deltaY < 0) return Math.min(s * factor, maxScale);
      return Math.max(s / factor, minScaleRef.current);
    });
  };
  // 핀치 줌 - 안드로이드 웹브라우저의 경우 이벤트에 등록된 경우 useState 값이 초기 설정 값으로 들어가는 문제가 있음
  const handleTouchMoveScale = (e: TouchEvent) => {
    if (e.touches.length !== 2 || aniIdRef.current !== null) return;
    // setIsTouching(true);

    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (lastPinchDistance.current) {
      const diff = distance - lastPinchDistance.current;
      const checkMinScale = minScaleRef.current;
      if (Math.abs(diff) > 2) {
        setScale((s) => {
          const moveScale = 1.1;
          const newScale = diff > 0 ? Math.min(s * moveScale, maxScale) : Math.max(s / moveScale, checkMinScale);
          return newScale;
        });
      }
    }
    lastPinchDistance.current = distance;
  };
  const handleTouchEnd = () => {
    setIsTouching(false);
    dragStart.current = null;
    lastPinchDistance.current = null;
  };
  const handleTouchStart = (event: ReacthTouch) => {
    const e = event.touches[0];
    if (checkAndroid) {
      layoutRef.current?.classList.add("TouchMoveChart");
      if (window.scrollY < 1) {
        window.scrollTo({ top: 1, behavior: "instant" });
      }
    }
    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    dragStart.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseMove = (event: React.MouseEvent | TouchEvent) => {
    setIsTouching(true);
    const e = event instanceof TouchEvent ? event.touches[0] : event;
    const isTouch = event instanceof TouchEvent;
    if (!dragStart.current) {
      if (event instanceof TouchEvent) {
        dragStart.current = { x: e.clientX, y: e.clientY };
      }
      return;
    }
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    const nowScale = scaleRef.current;
    const scaleFactor = 1.3; //isTouch ? 0.5 / (nowScale / getMinScale()) ** (1 / 2) : 1; //제대로 적용되는 보정값 찾아야 할듯

    // projectionScale 기반 보정

    // animateProjection({ rotation: [rotation[0] + dLon, Math.max(-90, Math.min(90, rotation[1] - dLat)), rotation[2]], duration: 0 });
    // setScale(scale);
    setRotation((r) => {
      const k = Math.cos((r[1] * Math.PI) / 180); // 위도 보정
      const dLon = r[0] + (((dx / (2 * Math.PI * nowScale)) * 360) / Math.max(k, 100 / nowScale)) * scaleFactor;
      const dLat = (dy / (Math.PI * nowScale)) * 180 * scaleFactor;
      const toLon = dLon < -180 || dLon > 180 ? ((dLon + 540) % 360) - 180 : dLon;
      return [toLon, Math.max(-90, Math.min(90, r[1] - dLat)), r[2]];
    });

    dragStart.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseUp = () => {
    setIsTouching(false);
    dragStart.current = null;
    layoutRef.current?.classList.remove("TouchMoveChart");
  };
  //줌인아웃 관련 함수 끝
  const setMinScaleOut = (set_scale: number) => {
    if (set_scale > 0) {
      minScaleRef.current = set_scale;
      if (scale < set_scale) {
        setScale(set_scale);
      }
    }
  };
  const getGeoPosition = (geoFeatures: GeoFeature[]) => {
    const lats: number[] = [];
    const lngs: number[] = [];
    geoFeatures.forEach((country) => {
      const coordinates = country.geometry.coordinates;
      coordinates.forEach((m) => {
        m.forEach((coord) => {
          if (coord.length > 0) {
            if (coord[0] instanceof Array) {
              coord.forEach((item) => {
                if (item instanceof Array) {
                  // const lng = item[0] >= 0 ? item[0] :
                  lngs.push(item[0]);
                  lats.push(item[1]);
                }
              });
            } else {
              lngs.push(coord[0]);
              if (!(coord[1] instanceof Array)) {
                lats.push(coord[1]);
              }
            }
          }
        });
      });
      //const lngs = coordinates.map((m) => m[1]);
    });
    const latMin = Math.min(...lats);
    const latMax = Math.max(...lats);
    const checklngMin = Math.min(...lngs);
    const checklngMax = Math.max(...lngs);
    const checkLngs =
      checklngMin < 0 && checklngMax > 0 && checklngMax - checklngMin > 180
        ? lngs.map((value) => (value < 0 ? value + 360 : value))
        : undefined;
    const lngMin = checkLngs ? Math.min(...checkLngs) : checklngMin;
    const lngMax = checkLngs ? Math.max(...checkLngs) : checklngMax;
    const latCenter = (latMin + latMax) / 2;
    const lngCenter = (lngMin + lngMax) / 2;
    const lngDelta = ((lngMax - lngMin) * Math.PI) / 180;
    const latDelta = ((latMax - latMin) * Math.PI) / 180;
    const checkwidth = layoutRef.current?.getBoundingClientRect().width ?? minScaleRef.current;
    const checkheight = layoutRef.current?.getBoundingClientRect().height ?? minScaleRef.current;
    // const maxl = Math.max(((latMax - latMin) / 180) * checkwidth, ((lngMax - lngMin) / 180) * minScale);

    const checkScaleX = checkwidth / (lngDelta * Math.cos((latCenter * Math.PI) / 180)) / 1.5;
    const checkScaleY = checkheight / latDelta / 1.5;
    const checkScale = Math.min(checkScaleX, checkScaleY);

    const lambda = rotation[0] + normalizeAngle((geoFeatures.length > 0 ? -lngCenter : rotation[0]) - rotation[0]);
    const phi = geoFeatures.length > 0 ? -latCenter : rotation[1];
    // if (selectCountryListFeatures.length) {
    //   Debug("selectCountryListFeatures", { latMax, latMin, lngMax, lngMin });
    // }
    Debug("selectCountryListFeatures", {
      start: rotation[0],
      lambda,
      lngMax,
      lngMin,
      checklngMax,
      checklngMin,
    });
    const temp_scale = geoFeatures.length > 0 ? checkScale : minScaleRef.current;
    return { lambda, phi, scale: temp_scale };
  };

  //초기 설정
  useEffect(() => {
    //import(`/messages/${locale}.json`)
    import(`@/datas/map/world_countries.json`)
      .then((mod) => setCountries(mod.default))
      .catch((error) => {
        // console.error("Failed to load messages", error);
        return {};
      });

    const el = layoutRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (aniIdRef.current !== null) return;
      e.preventDefault();
      handleWheelScale(e);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (aniIdRef.current !== null) return;
      if (e.touches.length === 1) {
        e.preventDefault();
        handleMouseMove(e);
      } else if (e.touches.length === 2) {
        e.preventDefault();
        handleTouchMoveScale(e);
      }
    };

    // passive:false 로 wheel 이벤트 등록
    // el.addEventListener("wheel", handleWheel, { passive: false });
    // if (Capacitor.isNativePlatform()) {
    // }
    el.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      // el.removeEventListener("wheel", handleWheel);
      // if (Capacitor.isNativePlatform()) {
      // }
      if (el) {
        el.removeEventListener("touchmove", handleTouchMove);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!layoutRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { height } = entry.contentRect;
        const checkMinScale = (height - 4) / 2;
        setMinScaleOut(checkMinScale);
      }
    });
    observer.observe(layoutRef.current);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //선택 국가 변경시
  useEffect(() => {
    if (minScaleRef.current <= 0) return;
    // const country_list = getCountryList();
    const select_country_list = getSelectCountryList() ?? [];
    const country_list = [...new Set([...getCountryList(), ...select_country_list])];
    // Debug("check country list", country_list, select_country_list);
    const countryListFeatures: GeoFeature[] = countries.features
      .filter((feature) => country_list.includes(feature.id))
      .map((feature) => feature);
    const selectCountryListFeatures: GeoFeature[] = countries.features
      .filter((feature) => select_country_list.includes(feature.id))
      .map((feature) => feature);
    const geoposition = getGeoPosition(selectCountryListFeatures);
    setSelectFeature(countryListFeatures);
    // Debug("change", temp_scale, minScale, checkScale, checkScaleX, checkScaleY, checkwidth, checkheight);
    const temp_data = countryListFeatures.map((select) => {
      const check_select = select_country_list.includes(select.id);
      return {
        id: select.id,
        value: check_select ? 100 : 70,
        color: fillColor,
        properties: select.properties,
        label:
          params.locale === enLocale
            ? select.properties.name
            : ((select.properties as any)["name_" + params.locale] ?? select.properties.name),
      };
    });
    const duration = latlngDuration(rotation[1], rotation[0], geoposition.phi, geoposition.lambda) * 2;
    const aniProp: proAniProps = { rotation: [geoposition.lambda, geoposition.phi, 0], duration };

    if (!selectCountryRef.current) {
      aniProp.scale = Math.min(Math.max(geoposition.scale, minScaleRef.current), maxScale);
      aniProp.fromScale = Math.min(Math.max(geoposition.scale / 1.5, minScaleRef.current), maxScale);
      aniProp.fromRotation = [geoposition.lambda, geoposition.phi, 0];
    } else if (scale > geoposition.scale || (rotation[0] == geoposition.lambda && rotation[1] === geoposition.phi) || params.mapRescale) {
      aniProp.scale = Math.min(Math.max(geoposition.scale, minScaleRef.current), maxScale);
      if (rotation[0] == geoposition.lambda && rotation[1] === geoposition.phi) {
        aniProp.easeType = "easeLinear";
      }
    }
    // aniProp.duration = duration * 2;

    animateProjection(aniProp);
    setTimeout(() => {
      setData(temp_data);
    }, duration / 2);
    selectCountryRef.current = selectCountryListFeatures.length > 0 ? select_country_list : undefined;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries.features, params.locale, params.country_label, params.country_list, params.select_country, minScaleRef.current]);

  const ta = useTranslations("alert");
  const alertContext = useContext(AlertContext);

  const GetButtons = () => {
    return (
      <Flex className={cn("flex-row-reverse w-full text-right relative top-2 right-2 z-10 h-0")}>
        {(params.closeButton ?? true) ? (
          <Button
            className="ml-2"
            shape="circle"
            icon={<Icon iconType="" iconName="X" weight="bold" />}
            onClick={() => {
              params.setSelectCountry(undefined);
            }}
          ></Button>
        ) : (
          ""
        )}
        {params.imageDownload ? (
          <Button
            className={cn("ml-2")}
            icon={<Icon iconType="" iconName="DownloadSimple" weight="bold" />}
            shape="circle"
            onClick={() => {
              exportChartAsPNG(ta, alertContext, layoutRef.current, "CoffeeCountry", params.imageDownload);
            }}
          ></Button>
        ) : undefined}

        <Button
          icon={<Icon iconType="" iconName="FrameCorners" weight="bold" />}
          shape="circle"
          onClick={() => {
            // setScale((s) => Math.min(s * (1 + 1 / 3), maxScale));
            animateProjection({ scale: minScaleRef.current, easeType: "easeQuadOut" });
          }}
        ></Button>
        <Button
          className="mr-2"
          shape="circle"
          icon={<Icon iconType="" iconName="Minus" weight="bold" />}
          onClick={() => {
            // setScale((s) => Math.max(scale / (1 + 1 / 3), minScale));
            animateProjection({ scale: Math.max(scale / (1 + 1 / 3), minScaleRef.current), easeType: "easeQuadOut" });
          }}
        ></Button>
        <Button
          className="mr-2"
          shape="circle"
          icon={<Icon iconType="" iconName="Plus" weight="bold" />}
          onClick={() => {
            // setScale((s) => Math.min(s * (1 + 1 / 3), maxScale));
            animateProjection({ scale: Math.min(scale * (1 + 1 / 3), maxScale), easeType: "easeQuadOut" });
          }}
        ></Button>
        {!checkEmpty(params.select_country) ? (
          <Button
            className="mr-2"
            shape="circle"
            icon={<Icon iconType="" iconName="MapPinSimpleArea" weight="bold" />}
            onClick={() => {
              // setScale((s) => Math.min(s * (1 + 1 / 3), maxScale));
              const country = params.select_country
                ? typeof params.select_country === "string"
                  ? [params.select_country]
                  : [...params.select_country]
                : undefined;
              if (country) {
                params.setSelectCountry(country);
              }
            }}
          ></Button>
        ) : undefined}
        <Switch
          className="mr-auto my-1 ml-4"
          defaultChecked={!filter}
          onChange={(e) => {
            setFilter(!e);
          }}
        />
      </Flex>
    );
  };
  // Debug("projection", spring.scale, isTouching);
  // const projectionRotation: [number, number, number] = [
  //   rotationSpring.rotationX.get(),
  //   rotationSpring.rotationY.get(),
  //   rotationSpring.rotationZ.get(),
  // ];

  return (
    <Flex className="w-full flex-col">
      {GetButtons()}
      <Flex
        ref={layoutRef}
        className={cn("data-chart", "MapChart", params.className, defaultClassName, params.isMobile ? "isMobile" : "notMobile")}
        // onClick={}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        // onTouchMove={handleMouseMove}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
      >
        <ResponsiveChoropleth
          data={data}
          domain={[0, 100]}
          features={filter && selectFeatures.length > 0 ? selectFeatures : countries.features}
          theme={nivoTheme}
          projectionType="orthographic"
          projectionScale={scale}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          projectionTranslation={[0.5, 0.5]}
          projectionRotation={rotation}
          enableGraticule={true}
          onClick={(e) => {
            const geo = e as unknown as GeoFeature;
            if ((params.clickMove ?? true) && e !== undefined && geo) {
              const geoposition = getGeoPosition([geo]);
              const aniProp: proAniProps = { rotation: [geoposition.lambda, geoposition.phi, 0], duration: 1000 };
              const duration = latlngDuration(rotation[1], rotation[0], geoposition.phi, geoposition.lambda);
              aniProp.duration = duration;
              aniProp.easeType = "easeLinear";
              aniProp.scale = Math.min(Math.max(geoposition.scale, minScaleRef.current), maxScale);
              const country_list = getCountryList();
              const select_country = getSelectCountryList() ?? [];
              const select_country_list = [...new Set([...country_list, ...select_country, geo.id])];
              const countryListFeatures: GeoFeature[] = countries.features
                .filter((feature) => select_country_list.includes(feature.id))
                .map((feature) => feature);
              setSelectFeature(countryListFeatures);
              // Debug("change", temp_scale, minScale, checkScale, checkScaleX, checkScaleY, checkwidth, checkheight);
              const temp_data = countryListFeatures.map((select) => {
                const check_select = select.id === geo.id;
                return {
                  id: select.id,
                  value: check_select ? (country_list.includes(geo.id) || checkEmpty(select_country) ? 100 : 30) : 70,
                  color: fillColor,
                  properties: select.properties,
                  label:
                    params.locale === enLocale
                      ? select.properties.name
                      : ((select.properties as any)["name_" + params.locale] ?? select.properties.name),
                };
              });
              animateProjection(aniProp);
              // Debug(e, temp_data, country_list);

              setTimeout(() => {
                setData(temp_data);
              }, duration / 2);
            }
            if (params.onClick) {
              params.onClick(e);
            }
          }}
          //label="properties.name"
          label={(feature: any) => {
            //Debug(feature);
            return feature.data.label;
          }}
          //fillColor="var(--accent-a5)"
          // colors="OrRd"
          borderWidth={() => Math.min(60, Math.max(1.3, 120 / scale))}
          // unknownColor="var(--ant-color-bg-container-disabled)"
          // colors={["var(--accent-a2)", "var(--accent-a3)", "var(--accent-a4)", "var(--accent-a5)", "var(--accent-a6)"]}
          // borderColor="var(--gray-6)"
          // graticuleLineColor="var(--gray-a4)"
          unknownColor="#0000000a"
          colors={["#aa550009", "#a04b0018", "#9b4a0026", "#9f4d0035", "#a04e0048"]}
          borderColor="#d9d9d9"
          graticuleLineColor="#00000017"
          tooltip={
            dragStart.current
              ? (props: any) => {
                  return <></>;
                }
              : (props: any) => {
                  const label =
                    params.locale === enLocale
                      ? props.feature.properties.name
                      : ((props.feature.properties as any)["name_" + params.locale] ?? props.feature.properties.name);
                  if (props.feature.value !== undefined) {
                    //Debug(props);
                    return (
                      <div style={chartCoverDivstyle}>
                        <table>
                          <tbody>
                            <tr>
                              <td>
                                <span
                                  style={{
                                    display: "block",
                                    width: "1rem",
                                    height: "1rem",
                                    background: props.feature.data.color,
                                    marginRight: "0.5rem",
                                  }}
                                ></span>
                              </td>
                              <td>
                                <strong>{label}</strong>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  } else {
                    return <div style={chartCoverDivstyle}>{label}</div>;
                  }
                }
          }
        />
      </Flex>
    </Flex>
  );
}
export default function DataGeoMap(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const DataLabel: DataElement =
    typeof params.label === "string" || params.label === undefined
      ? { element: params.label ?? (params.labelNamePrev ?? "") + params.name }
      : params.label;
  // const [isAnimation, setAnimation] = useState(params.geoMapProp?.isAnimation ?? true);

  const [selectCountry, setSelectCountry] = useState<string | string[] | undefined>(
    (params.geoMapProp?.mapDisplay ?? true) || params.geoMapProp?.select_country
      ? typeof params.geoMapProp?.select_country === "string"
        ? getFieldValue(params.form, params.geoMapProp.select_country, params.values) //getInputFieldValue(params, params.geoMapProp.select_country)
        : params.geoMapProp?.select_country
      : undefined,
  );
  const geoMapProp: GeoMapItemProps = {
    ...params.geoMapProp,
    country_list: (params.geoMapProp?.country_list ?? params.indexLabelKey) ? undefined : params.indexKey,
    country_label: (params.geoMapProp?.country_label ?? (params.indexKey && params.indexLabelKey)) ? getDataKeyLabel(params) : undefined,
    t: t,
    scale: params.scale,
    filter: false,
    form: params.form,
    value: params.value,
    values: params.values,
    locale: params.locale,
    setSelectCountry: setSelectCountry,
    imageDownload: params.imageDownload,
  };

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    const temp_function = params.formFunction[params.name];
    if (temp_function) {
      // temp_function["setMapDisplay"] = setMapDisplay;
      // temp_function["getMapDisplay"] = () => mapDisplay;
      temp_function["setSelectCountry"] = setSelectCountry;
      temp_function["getSelectCountry"] = () => setSelectCountry;
    }
  }

  // useEffect(() => {
  //   setCountryLabel(getDataKeyLabel(params));
  //   // setFilter(false);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [params.values]);

  if (params.asChild ?? false) {
    return (
      <>
        {(params.labelUse ?? true) && (DataLabel.display ?? true) ? <GetDataLabel {...params} /> : ""}
        <Flex
          className={cn(
            params.geoMapProp?.className ?? defaultClassName,
            selectCountry ? " opacity-0 animate-fade animate-once animate-duration-150 animate-ease-in" : "hidden",
            "",
          )}
        >
          <DataGeoMapItem {...geoMapProp} select_country={selectCountry} onClick={params.onClick ? params.onClick : undefined} />
        </Flex>
        {params.geoMapProp?.emptyElement && !selectCountry ? (
          <Flex>{GetDataInputElement(params.geoMapProp?.emptyElement, false, null, t)}</Flex>
        ) : (
          ""
        )}
      </>
    );
  } else {
    const useLabel = (params.labelUse ?? true) && (DataLabel.display ?? true);
    const mergeValueStyle = useLabel ? params.valueStyle : { ...params.valueStyle, gridColumn: "1 / span 2" };
    return (
      <>
        <DataList.Item
          className={cn(
            params.itemClassName,
            params.nextItemHidden ? "next-item-hidden" : "",
            (params.show ?? true) ? "" : "hidden",
            selectCountry ? "opacity-0 animate-fade animate-once animate-duration-150 animate-ease-in" : "hidden",
          )}
        >
          <DataList.Label className={cn(params.labelClassName, useLabel ? "" : "hidden")} style={params.labelStyle}>
            <Skeleton loading={params.loading}>{useLabel ? <GetDataLabel {...params} /> : "&nbsp;"}</Skeleton>
          </DataList.Label>
          <DataList.Value className={params.valueClassName} style={mergeValueStyle}>
            <AntSkeleton loading={params.loading}>
              {prev}
              <DataGeoMapItem {...geoMapProp} select_country={selectCountry} onClick={params.onClick ? params.onClick : undefined} />
              {next}
            </AntSkeleton>
          </DataList.Value>
        </DataList.Item>
        {params.geoMapProp?.emptyElement && !selectCountry ? (
          <Flex>{GetDataInputElement(params.geoMapProp?.emptyElement, false, null, t)}</Flex>
        ) : (
          ""
        )}
      </>
    );
  }
}
